if ( typeof ProductQuantity !== 'function' ) {
	/**
	 * Section Class for quantity selectors component/snippet
	 * This class deals with product quantity change, plus and minus button click
	 * This class updates product quantity for each quantity component individually
	 * 
	 */
	class ProductQuantity extends HTMLElement {
		constructor(){
			super();

			this._container				= this;

			this._selectors = {
				activeRechargeOption	: '[data-radio-group-options] .rc_widget__option--active input[type="radio"]',
				rechargeButton			: '[data-recharge-show]',
				rechargeWrapper			: '[data-recharge-wrapper]',
				plansDropdown			: '[data-plans-dropdown]',
				qty						: '[data-qty-selector]',
				qtyMinus				: '[data-qty-minus]',
				qtyPlus					: '[data-qty-plus]'
			};

			// ELEMENTS
			this.qty					= this.querySelector(this._selectors.qty);
			this.qtyMinus				= this.querySelector(this._selectors.qtyMinus);
			this.qtyPlus				= this.querySelector(this._selectors.qtyPlus);
			this.rechargeButton			= this.querySelector(this._selectors.rechargeButton);
			this.rechargeWrapper		= this.querySelector(this._selectors.rechargeWrapper);
			this.qtyMin					= this.qty.getAttribute('min') ? parseInt(this.qty.getAttribute('min')) : 1;
			this.qtyMax					= this.qty.getAttribute('max') ? parseInt(this.qty.getAttribute('max')) : 999;
			this.cartJSON				= JSON.parse(document.querySelector('[data-cart-json]').innerHTML);

			this.init();
		}

		/**
		 * Initial Methods
		 */
		init() {
			this.updateInputQuantityFromCart();
			// 1. Check if we disabled the qty minus btn
			if (parseInt(this.qty.value) - 1 < this.qtyMin)
				this.qtyMinus.classList.add('disabled');

			// 2. Check if we disabled the qty plus btn
			if (parseInt(this.qty.value) + 1 > this.qtyMax)
				this.qtyPlus.classList.add('disabled');

			/* Add Debounce to event listener handlers */

			// 3. Check if qty is a INPUT tag to trigger a keyup listener
			if (this.qty.tagName === 'INPUT')
				this.qty.addEventListener('keyup', window.bluedgeusa.theme.Utils.debounce((e) => { this._inputChangeHandle(e) }, 260));
			else if (this.qty.tagName === 'SELECT')
				this.qty.addEventListener('change', (e) => this._inputChangeHandle(e));

			// 4. Qty minus btn event listener
			this.qtyMinus.addEventListener('click', window.bluedgeusa.theme.Utils.debounce((e) => { this._btnMinusHandle(e) }, 260));

			// 5. Qty plus btn event listener
			this.qtyPlus.addEventListener('click', window.bluedgeusa.theme.Utils.debounce((e) => { this._btnAddHandle(e) }, 260));

			// 6. Add custom event listeners
			if (this._container?.dataset.discountAtc) {
				this._container.addEventListener('collectionBundle:qtyChange', (e) => this._addItemsToCart(e));
				document.addEventListener('core:addToCart:success', this.refreshCart);
				document.addEventListener('core:changeToCart:success', this.refreshCart);
				document.addEventListener('core:changeToCart:delete:success', this.refreshCart);
				document.addEventListener('core:items:loaded', () => this.handleCartUpdate());
				if (this.rechargeWrapper)
					window.addEventListener('ReChargeWidget-loaded', () => { this.injectRechargeWidget() })
			}
		}

		/**
		 * On Qty input change handle
		 * @param {Event} e 
		 */
		_inputChangeHandle(e) {
			// 1. Check qty is reached max qty limit
			if (+this.qty.value > this.qtyMax)
				this.qty.value = this.qtyMax;
			else if (+this.qty.value === 0)
				this.qty.value = 0;
			else
				this.qty.value = parseInt(this.qty.value);

			// 2. Get current qty from qty input
			const currentQty = parseInt(this.qty.value);

			// 3. Enable or disable minus button
			if (currentQty - 1 < this.qtyMin) {
				this.qtyMinus.classList.add('disabled');
			} else {
				this.qtyMinus.classList.remove('disabled');
			}

			// 4. Enable or disable plus button
			if (currentQty + 1 > this.qtyMax) {
				this.qtyPlus.classList.add('disabled');
			} else {
				this.qtyPlus.classList.remove('disabled');
			}

			// 5. Trigger custom Event (for other component classes)
			this.triggerCustomEvent(this.qty.value, 'change', this.qty);
		}

		/**
		 * On quantity button plus click 
		 * @param {Event} e 
		 */
		_btnAddHandle(e) {
			e.preventDefault();

			// 1. Check plus qty is not disabled
			if (!this.qtyPlus.classList.contains('disabled')) {
				const currentQty = parseInt(this.qty.value);

				// 2. Enable or disable minus button
				if (currentQty + 1 <= this.qtyMax) {
					this.qty.value = currentQty + 1;
					this.qtyMinus.classList.remove('disabled');
				}

				// 3. Enable or disable plus button
				if (currentQty + 1 >= this.qtyMax) {
					this.qtyPlus.classList.add('disabled');
				}
			}

			// 4. Trigger custom Event
			this.triggerCustomEvent(this.qty.value, 'plus', this.qty);
		}

		/**
		 * On quantity minus button click handle
		 * @param {Event} e 
		 */
		_btnMinusHandle(e) {
			e.preventDefault();

			// 1. Check minus qty is not disabled
			if (!this.qtyMinus.classList.contains('disabled')) {
				const currentQty = parseInt(this.qty.value);

				// 2. Enable or disable plus button
				if (currentQty - 1 >= this.qtyMin) {
					this.qty.value = currentQty - 1;
					this.qtyPlus.classList.remove('disabled');
				}

				// 3. Enable or disable minus button
				if (currentQty - 1 <= this.qtyMin) {
					this.qtyMinus.classList.add('disabled');
				}
			}

			// 4. Trigger custom Event
			this.triggerCustomEvent(this.qty.value, 'minus', this.qty);
		}

		/**
		 * Handles adding/removing items to cart
		 * based on input event
		 */
		_addItemsToCart(e) {
			if (!e?.detail) return false;

			// If adding item
			if (e.detail.action === 'plus') {
				// Create item array
				const items = [
					{
						quantity: 1,
						id: e.detail.target.dataset.variantId,
						selling_plan: this._getSelectedSellingPlanFromRecharge()
					}
				]
				// Add to cart
				return window.bluedgeusa.theme.Utils.addToCart(items);
			} else {
				// Make sure cart json is up to date
				this.cartJSON = JSON.parse(document.querySelector('[data-cart-json]').innerHTML);
				// Find if item already exists in cart
				const item = this.cartJSON.items.find(cartItem => cartItem.variant_id == e.detail.target.dataset.variantId);
				// Default to minus
				let action = 'minus'
				// Change to remove if incoming quantity is 0
				if (e.detail.quantity === 0)
					action = 'remove'
				// If item does not already exist in cart, we add to cart (happens when event is from input rather than plus or minus)
				if (!item?.key) {
					const items = [
						{
							quantity: e.detail.quantity,
							id: e.detail.target.dataset.variantId,
							selling_plan: this._getSelectedSellingPlanFromRecharge()
						}
					]
					return window.bluedgeusa.theme.Utils.addToCart(items);
				} else {
					// If item does exist, we change to cart
					return window.bluedgeusa.theme.Utils.changeToCart(e.detail.quantity, item.key, item.properties, item.selling_plan, action);
				}
			}
		}

		/**
		 * Refresh cart
		*/
		refreshCart() {
			// Use Theme method to update drawer cart
			window.refreshCart();
		}

		handleCartUpdate() {
			this.updateCartJSON();
			this.updateInputQuantityFromCart();
		}

		/**
		 * Update quantity if item in cart
		 */
		updateInputQuantityFromCart() {
			// Only apply to specific quantity inputs
			if (!this._container.dataset.discountAtc) return false;
			// If the cart is empty, we default to 0
			if (!this.cartJSON?.items?.length) return this.qty.value = 0;
			// Find if there is this product in cart
			const item = this.cartJSON.items.find(cartItem => this.qty.dataset.variantId == cartItem.variant_id);

			// If there is, update the quantity to match number in cart
			if (item)
				this.qty.value = item.quantity;
			// If not, item is 0
			else
				this.qty.value = 0;
		}

		/**
		 * Update cart JSON
		 */
		updateCartJSON() {
			this.cartJSON = JSON.parse(document.querySelector('[data-cart-json]').innerHTML);
		}


		/**
		 * Trigger Custom event right after qty updated
		 * This custom event is used on other component classes
		 *
		 * @param {Number} qty 
		 * @param {String} action 
		 * @param {Element} target 
		 */
		triggerCustomEvent(qty, action, target) {
			// 1. Collect closest collection grid component element
			const gridWrapper = this.closest('collection-grid');

			this._container.dispatchEvent(
				new CustomEvent('collectionBundle:qtyChange', { detail: { quantity: qty, action: action, target: target } })
			);
			
			if (!gridWrapper)
				return;

			// 2. Dispatch new event to collection grid
			gridWrapper.dispatchEvent(
				new CustomEvent('collectionBundle:qtyChange', { detail: { quantity: qty, action: action, target: target } })
			);
		}

		/**
		 * Inject the subscription widget from shopify based on current product ID
		 */
		injectRechargeWidget() {
			// 1. Check if Main Recharge Widget Exists on page
			if (!window?.ReChargeWidget)
				return;

			// 2. Check if a widget already exists using widget from recharge method
			const widgetExists = window.ReChargeWidget.getWidgetsByProductId(this.qty.dataset.variantId);
			const productId = this.qty.dataset.productId;
			const parent = `[data-recharge-wrapper="${this.qty.dataset.variantId}"]`;

			if (widgetExists?.length)
				return;

			const rechargeConfig = {
				productId: productId, // Required
				injectionParent: parent, // The node that will have the widget injected in
				injectionMethod: 'append', // How will the widget be injected
			};

			if (this.rechargeWrapper)
				this.rechargeWrapper.classList.remove('hidden');

			// 4. create widget using the reCharge JS widget and call the inject method from recharge widget
			window.ReChargeWidget.createWidget(rechargeConfig);

			// 5. Enable modal button + remove classes
			const alertClasses = ['alert', 'alert--note', 'alert--circle', 'alert--circle-loading'];
			// Check if button exists
			if (this.rechargeButton) {
				this.rechargeButton.classList.remove(...alertClasses);
				this.rechargeButton.disabled = false;
			}
				

		}

		/**
		 * Return Selling plan depending on Recharge selection
		 * @returns {Number}
		 */
		_getSelectedSellingPlanFromRecharge() {
			// 1. Get Recharge Radio Group element to check if is a one time subscription
			const rechargeRadioGroups = this._container.querySelector(this._selectors.activeRechargeOption);

			// 2. If not a one time subscription check for the delivery plan option
			if (rechargeRadioGroups?.value !== 'onetime') {
				// 2.1 Get selling plan selector from recharge (frequency selector)
				const selectedSellingPlan = this._container.querySelector(this._selectors.plansDropdown);

				if (!selectedSellingPlan)
					return null;

				// 2.2 Return the selected Selling plan
				return selectedSellingPlan.value;
			} else {
				return null;
			}
		}
	}

	// Init Class
	if ( typeof customElements.get('product-quantity') == 'undefined' ) {
		customElements.define('product-quantity', ProductQuantity);
	}
}